import React, { useCallback, useRef, useState } from 'react';
import {
  RenderElementProps,
  useFocused,
  useSelected,
  useSlate,
} from 'slate-react';
import styled, { css } from 'styled-components';
import ELEMENTS from '~/components/organism/PluginsEditor/components/elements/elementsEnum';
import { SignatureElement } from '~/components/organism/PluginsEditor/types';
import serialize from './serialize';
import { Editor, Element } from 'slate';
import TEST_ID from './index.testid';
import selectSignature from '~/components/organism/PluginsEditor/commands/modify/signature';
import TextButton from '~/components/atom/TextButton';
import SignatureDropdownList from '~/components/molecule/SignatureDropdownList';

export type Props = RenderElementProps & {
  element: SignatureElement;
};

const text = {
  label: 'Selecteer handtekening',
};

export const Signature: React.FC<Props> = ({
  attributes,
  element,
  children,
}) => {
  const editor = useSlate();
  const selected = useSelected();
  const focused = useFocused();
  const active = selected && focused;

  const [isOpen, setIsOpen] = useState(false);

  const isEmpty = Editor.isEmpty(editor, element);
  const isOnlyChildEmpty =
    element.children &&
    element.children.length === 1 &&
    Element.isElement(element.children[0]) &&
    Editor.isEmpty(editor, element.children[0]);

  const noBorder = (isEmpty || isOnlyChildEmpty) && !active;

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const onOptionChange = useCallback(
    option => {
      const html = option?.payload?.html;
      const attachments = option.payload?.attachments;

      selectSignature({
        editor,
        html: html ?? null,
        attachments,
      });
    },
    [editor],
  );

  return (
    <Outer
      {...attributes}
      {...element.attributes}
      data-testid={TEST_ID.CONTAINER}
    >
      <Container $hasBorder={!noBorder}>
        <StyledButton
          icon="pencil"
          onClick={() => setIsOpen(true)}
          size="small"
          contentEditable={false}
          label={text.label}
          dataTestId={TEST_ID.SELECT_SIGNATURE_BUTTON}
          ref={buttonRef}
        />

        <SignatureDropdownList
          isOpen={isOpen}
          onChange={onOptionChange}
          onClose={() => setIsOpen(false)}
          ref={buttonRef}
        />
        {children}
      </Container>
    </Outer>
  );
};

const Outer = styled.div<{}>`
  position: relative;
`;

const StyledButton = styled(TextButton)<{}>(
  () => css`
    float: right;
  `,
);

const Container = styled.div<{ $hasBorder }>(
  ({ theme, $hasBorder }) => css`
    border-radius: ${theme.getTokens().border.radius.base};
    padding: ${theme.space('xxxs')};
    border: 1px solid transparent;

    &:hover {
      border: ${$hasBorder
        ? `1px dashed ${theme.color('tertiary')}`
        : '1px solid transparent'};
    }
  `,
);

export default {
  nodeName: 'DHSIGNATURE',
  renderComponent: props => <Signature {...props} />,
  deserialize: () => ({
    type: ELEMENTS.SIGNATURE,
  }),
  serialize,
};
